import { useEffect, useState } from "react";
import { adminUpdateSettings } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { AMNAYA_PEETHAS, SHANKARA_IMG } from "@/lib/amnayaPeethas";
import ImageUploader from "@/components/ImageUploader";
import { Landmark, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const inp = "w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none";

function PeethaBlock({ p, data, onChange }) {
  const set = (k, v) => onChange(p.slug, { ...data, [k]: v });
  return (
    <div className="border border-line bg-surface p-5" data-testid={`amnaya-edit-${p.slug}`}>
      <div className="mb-3 flex items-center gap-2">
        <span className="rounded-full bg-saffron/10 px-2.5 py-1 text-xs font-medium text-saffron">{p.direction_en}</span>
        <p className="font-serif-latin text-lg text-ink">{p.name}</p>
      </div>
      <div className="grid gap-4 md:grid-cols-[180px_1fr]">
        <div>
          <p className="mb-1.5 text-xs text-ink-3">Image</p>
          <ImageUploader value={data.image || p.image} onChange={(v) => set("image", v)} />
        </div>
        <div className="space-y-3">
          <label className="block text-xs text-ink-3">Current Jagadguru
            <input className={inp} value={data.current_acharya ?? p.current_acharya} onChange={(e) => set("current_acharya", e.target.value)} data-testid={`amnaya-acharya-${p.slug}`} />
          </label>
          <label className="block text-xs text-ink-3">Predecessor / successor note
            <input className={inp} value={data.predecessor ?? p.predecessor} onChange={(e) => set("predecessor", e.target.value)} />
          </label>
          <label className="block text-xs text-ink-3">Official website
            <input className={inp} value={data.website ?? p.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" />
          </label>
          <label className="block text-xs text-ink-3">Summary (shown on the page)
            <textarea rows={3} className={inp} value={data.summary ?? p.summary} onChange={(e) => set("summary", e.target.value)} />
          </label>
          <label className="block text-xs text-ink-3">Extra note (optional, shown under the Acharya)
            <textarea rows={2} className={inp} value={data.note ?? ""} onChange={(e) => set("note", e.target.value)} />
          </label>
        </div>
      </div>
    </div>
  );
}

export default function AdminAmnaya() {
  const settings = useSettings();
  const [data, setData] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setData(settings?.amnaya_peethas || {}); }, [settings?.amnaya_peethas]);

  const onChange = (slug, val) => setData((d) => ({ ...d, [slug]: val }));
  const setShankara = (k, v) => setData((d) => ({ ...d, _shankara: { ...(d._shankara || {}), [k]: v } }));

  const save = async () => {
    setBusy(true);
    try {
      await adminUpdateSettings({ amnaya_peethas: data });
      toast("Amnaya Peethas saved", { description: "Run Sync (Preview → Live) to publish." });
    } catch (e) { toast.error("Save failed", { description: e.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  };

  const sh = data._shankara || {};

  return (
    <div className="mx-auto max-w-3xl" data-testid="admin-amnaya">
      <div className="mb-2 flex items-center gap-2.5"><Landmark className="h-6 w-6 text-saffron" /><h1 className="font-serif-latin text-3xl text-ink">Amnaya Peethas</h1></div>
      <p className="mb-6 text-sm text-ink-3">Edit the four Āmnāya Pīṭha pages — set the current Jagadguru (editable as successions change), images, summaries and official links. Scriptural facts (Veda, Mahāvākya, first Acharya) are fixed in tradition.</p>

      <div className="mb-6 border border-line bg-surface p-5" data-testid="amnaya-edit-shankara">
        <p className="mb-3 font-serif-latin text-lg text-ink">Sri Adi Shankaracharya — hero image</p>
        <div className="max-w-[180px]"><ImageUploader value={sh.image || SHANKARA_IMG} onChange={(v) => setShankara("image", v)} /></div>
      </div>

      <div className="space-y-5">
        {AMNAYA_PEETHAS.map((p) => <PeethaBlock key={p.slug} p={p} data={data[p.slug] || {}} onChange={onChange} />)}
      </div>

      <div className="sticky bottom-4 mt-6">
        <button onClick={save} disabled={busy} data-testid="amnaya-save" className="inline-flex items-center gap-2 bg-[var(--saffron)] px-6 py-3 text-sm font-medium text-white shadow-lg hover:bg-[var(--saffron-dark)] disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save all Amnaya Peethas
        </button>
      </div>
    </div>
  );
}
